import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StructuredData from "@/components/feature/StructuredData";
import type { BreadcrumbItem } from "@/components/feature/Breadcrumb";

const breadcrumbItems: BreadcrumbItem[] = [
  { label: "首页", href: "/" },
  { label: "关于我们" },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于我们 - P站视频指南",
  description: "P站视频指南是一个专注于视频平台使用教程和内容推荐的独立内容网站。我们致力于为用户提供最全面、实用的P站视频使用指南。",
};

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <StructuredData data={aboutSchema} />
      <Navbar />
      <main className="pt-24 md:pt-28 pb-14 md:pb-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="mb-14 md:mb-20">
              <h1
                className="text-2xl md:text-4xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                P站视频指南是一个专注于视频平台使用教程和内容推荐的独立内容网站。我们致力于为用户提供最全面、实用的P站视频使用指南，帮助每一位用户更好地使用平台，发现更多优质内容。
              </p>
            </section>

            <section className="mb-14 md:mb-20">
              <h2
                className="text-xl md:text-3xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                品牌介绍
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-3">我们的使命</h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    P站视频指南的使命是让每一位用户都能轻松掌握视频平台的使用技巧，发现感兴趣的内容。我们通过精心编写的教程文章、实用的搜索技巧和热门内容推荐，帮助用户最大化地利用平台功能，获得更好的使用体验。
                  </p>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-3">我们的愿景</h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    我们希望成为中文互联网上最权威的P站视频使用指南平台。随着平台的不断发展和内容的不断丰富，我们将持续为用户提供最新、最有价值的信息和教程。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20creative%20office%20workspace%20with%20warm%20ambient%20lighting%2C%20team%20collaboration%20atmosphere%2C%20clean%20wooden%20desk%20with%20laptop%2C%20soft%20natural%20light%20from%20window%2C%20editorial%20photography%20style%2C%20neutral%20tones&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="P站视频指南 - 品牌形象"
                    className="w-full h-full object-cover"
                    title="P站视频指南品牌形象"
                  />
                </div>
              </div>
            </section>

            <section className="mb-14 md:mb-20">
              <h2
                className="text-xl md:text-3xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                我们的声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-4">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-primary-500 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                    50万+
                  </div>
                  <p className="text-sm text-foreground-600">月活跃读者</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 text-accent-600 rounded-full mb-4">
                    <i className="ri-article-line text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-accent-500 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                    200+
                  </div>
                  <p className="text-sm text-foreground-600">原创文章</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-full mb-4">
                    <i className="ri-thumb-up-line text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-secondary-500 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                    98%
                  </div>
                  <p className="text-sm text-foreground-600">读者好评率</p>
                </div>
              </div>
            </section>

            <section>
              <h2
                className="text-xl md:text-3xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                联系我们
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">电子邮箱</p>
                          <p className="text-sm text-foreground-800">contact@yibanbj.cn</p>
                        </div>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0">
                          <i className="ri-global-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">官方网站</p>
                          <p className="text-sm text-foreground-800">www.yibanbj.cn</p>
                        </div>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">办公地址</p>
                          <p className="text-sm text-foreground-800">中国上海市浦东新区科技园区</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-4">工作时间</h3>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex justify-between py-2 border-b border-background-200/70">
                        <span>周一至周五</span>
                        <span>9:00 - 18:00</span>
                      </li>
                      <li className="flex justify-between py-2 border-b border-background-200/70">
                        <span>周六</span>
                        <span>10:00 - 16:00</span>
                      </li>
                      <li className="flex justify-between py-2">
                        <span>周日</span>
                        <span className="text-foreground-400">休息</span>
                      </li>
                    </ul>
                    <p className="text-xs text-foreground-500 mt-4">
                      如有任何问题或建议，欢迎通过电子邮件与我们联系。我们将在工作时间内尽快回复您的邮件。
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}